import type { MediaPostStatus, TaskPriority, TaskStatus } from '@ukinfo/shared';

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const;

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  return `${formatDate(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function formatShort(value: string | Date | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  return `${WEEKDAYS[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function formatTime(value: string | Date): string {
  const d = new Date(value);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "in 3 Tagen", "heute", "vor 2 Tagen" */
export function relativeDays(value: string | Date | null | undefined): string {
  if (!value) return '';
  const target = new Date(value);
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return 'heute';
  if (days === 1) return 'morgen';
  if (days === -1) return 'gestern';
  if (days > 0) return `in ${days} Tagen`;
  return `vor ${Math.abs(days)} Tagen`;
}

/** Wandelt ein ISO-Datum in den Wert eines datetime-local-Feldes. */
export function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fromDateInput(value: string, endOfDay = false): string | null {
  if (!value) return null;
  const d = new Date(`${value}T${endOfDay ? '23:59' : '12:00'}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency }).format(amount);
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  OPEN: 'Offen',
  IN_PROGRESS: 'In Arbeit',
  WAITING: 'Wartet',
  DONE: 'Erledigt',
  CANCELLED: 'Abgebrochen',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  LOW: 'Niedrig',
  NORMAL: 'Normal',
  HIGH: 'Hoch',
  URGENT: 'Dringend',
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  MATCH: 'Spiel',
  TRAINING: 'Training',
  MEETING: 'Sitzung',
  EVENT: 'Veranstaltung',
  TASK_DEADLINE: 'Aufgaben-Deadline',
  SPONSOR: 'Sponsorentermin',
  OTHER: 'Sonstiges',
};

export const MEDIA_STATUS_LABELS: Record<MediaPostStatus, string> = {
  IDEA: 'Idee',
  DRAFT: 'Entwurf',
  REVIEW: 'In Freigabe',
  APPROVED: 'Freigegeben',
  PUBLISHED: 'Veröffentlicht',
  ARCHIVED: 'Archiviert',
};

export const MEDIA_CHANNEL_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  WEBSITE: 'Website',
  NEWSLETTER: 'Newsletter',
  PRESSE: 'Presse',
  WHATSAPP: 'WhatsApp',
};

export const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  STATUTEN: 'Statuten',
  PROTOKOLL: 'Protokoll',
  FORMULAR: 'Formular',
  ANLEITUNG: 'Anleitung',
  CHECKLISTE: 'Checkliste',
  SPONSORING: 'Sponsoring',
  TURNIER: 'Turnier',
  RICHTLINIE: 'Richtlinie',
  TRAINING: 'Training',
  SONSTIGES: 'Sonstiges',
};

export const BENEFIT_TYPE_LABELS: Record<string, string> = {
  BANDENWERBUNG: 'Bandenwerbung',
  TRIKOTLOGO: 'Trikotlogo',
  WEBSITE: 'Website',
  SOCIAL_MEDIA: 'Social Media',
  MATCHSPONSOR: 'Matchsponsor',
  PLAKAT: 'Plakat',
  TURNIERWERBUNG: 'Turnierwerbung',
  VIP_EINLADUNG: 'VIP-Einladung',
  DURCHSAGE: 'Durchsage',
  SONSTIGES: 'Sonstiges',
};

export const BENEFIT_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Offen',
  IN_PROGRESS: 'In Arbeit',
  DELIVERED: 'Erfüllt',
  CANCELLED: 'Storniert',
};

export const SPONSOR_STATUS_LABELS: Record<string, string> = {
  PROSPECT: 'Interessent',
  ACTIVE: 'Aktiv',
  PAUSED: 'Pausiert',
  ENDED: 'Beendet',
};

export const SPONSORSHIP_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Entwurf',
  ACTIVE: 'Aktiv',
  EXPIRING: 'Läuft aus',
  ENDED: 'Beendet',
  CANCELLED: 'Storniert',
};

export const ORG_UNIT_TYPE_LABELS: Record<string, string> = {
  CLUB: 'Verein',
  BOARD: 'Vorstand',
  SECTION: 'Sektion',
  GROUP: 'Gruppe',
  TEAM: 'Mannschaft',
  COMMITTEE: 'Ausschuss',
  OTHER: 'Sonstiges',
};

export const MATCH_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Angesetzt',
  POSTPONED: 'Verschoben',
  CANCELLED: 'Abgesagt',
  PLAYED: 'Gespielt',
};

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  TASK_ASSIGNED: 'Aufgabe wurde mir zugewiesen',
  TASK_UNASSIGNED: 'Aufgabe in meinem Bereich ohne Zuständige',
  TASK_DUE_TOMORROW: 'Aufgabe ist morgen fällig',
  TASK_OVERDUE: 'Aufgabe ist überfällig',
  TASK_COMMENT: 'Neuer Kommentar zu einer Aufgabe',
  DOCUMENT_APPROVAL: 'Dokument benötigt Freigabe',
  MEDIA_APPROVAL: 'Medienbeitrag benötigt Freigabe',
  SPONSORSHIP_EXPIRING: 'Sponsorvertrag läuft aus',
  MATCH_CHANGED: 'Spieltermin wurde geändert',
  SPORT_RESPONSE_DUE: 'Sport-Rückmeldung ist noch offen',
  SPORT_ATTENDANCE_UNCONFIRMED: 'Sport-Anwesenheitsliste ist noch offen',
  PERMISSION_CHANGED: 'Meine Zugriffsrechte wurden geändert',
  SPONSOR_REQUEST: 'Anfrage an einen von mir betreuten Sponsor',
  POLL_REQUESTED: 'Neue Umfrage oder Erinnerung an eine Umfrage',
  TRAINING_REMARK: 'Neuer Hinweis zu einer Übung (für Verantwortliche der Trainingsplanung)',
};

export function statusTone(status: TaskStatus): 'neutral' | 'brand' | 'success' | 'warning' {
  switch (status) {
    case 'DONE':
      return 'success';
    case 'IN_PROGRESS':
      return 'brand';
    case 'WAITING':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function priorityTone(priority: TaskPriority): 'neutral' | 'warning' | 'danger' {
  if (priority === 'URGENT') return 'danger';
  if (priority === 'HIGH') return 'warning';
  return 'neutral';
}

export const RSVP_LABELS: Record<string, string> = {
  YES: 'Zugesagt',
  NO: 'Abgesagt',
  MAYBE: 'Vielleicht',
  PENDING: 'Offen',
};

export const ATTENDANCE_LABELS: Record<string, string> = {
  PRESENT: 'Anwesend',
  ABSENT: 'Gefehlt',
  EXCUSED: 'Entschuldigt',
  UNKNOWN: 'Offen',
};

export const ABSENCE_REASON_LABELS: Record<string, string> = {
  NONE: 'Kein Grund angegeben',
  INJURED: 'Verletzt',
  ILL: 'Krank',
  WORK: 'Beruflich',
  SCHOOL: 'Schule / Studium',
  VACATION: 'Urlaub',
  FAMILY: 'Familiär',
  OTHER: 'Sonstiges',
};

export const MEDIA_KIND_LABELS: Record<string, string> = {
  PHOTO: 'Foto',
  VIDEO: 'Video',
  LOGO: 'Logo',
  GRAPHIC: 'Grafik',
  TEMPLATE: 'Vorlage',
  DOCUMENT: 'Dokument',
  OTHER: 'Sonstiges',
};

export const JOB_RUN_STATUS_LABELS: Record<string, string> = {
  OK: 'Erfolgreich',
  ERROR: 'Fehler',
  RUNNING: 'Läuft',
};
